"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PhoneIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneChips, PhoneEditor } from "@/components/phone-fields";
import { api } from "@/lib/api";
import { birthdayInputValue, daysUntilNextBirthday, formatBirthdayUk, kyivDate, normalizeBirthday } from "@/lib/kyiv";
import { openTelegramUrl, requestOwnTelegramPhone, wait } from "@/lib/contacts";
import { givenName } from "@/lib/names";
import { joinPhones, normalizePhone, phoneTelHref } from "@/lib/phone";
import { isAdmin, isParentRole } from "@/lib/roles";
import type { MeResponse, StaffContactDto } from "@/lib/types";
import { StatusScreen } from "@/components/status-screen";
import { useMeActions } from "@/components/me-provider";
import { useLiveReload } from "@/components/live-provider";
import { SmoothReveal } from "@/components/smooth-reveal";
import { cn } from "@/lib/utils";

type Kid = {
  id: string;
  name: string;
  gender: "boy" | "girl";
  birthday: string;
  phone: string;
  contactName: string;
  contactPhone: string;
  canEditPhone: boolean;
  active: boolean;
  parents: Array<{
    id: string;
    name: string;
    phone: string;
    username: string | null;
    telegramId: string;
  }>;
};

type ParentOption = { id: string; name: string };

type KidsSort = "name" | "birthday";

function sortKids(kids: Kid[], sort: KidsSort): Kid[] {
  const today = kyivDate();
  return [...kids].sort((a, b) => {
    if (sort === "birthday") {
      const left = daysUntilNextBirthday(a.birthday, today);
      const right = daysUntilNextBirthday(b.birthday, today);
      if (left === null && right === null) return a.name.localeCompare(b.name, "uk");
      if (left === null) return 1;
      if (right === null) return -1;
      if (left !== right) return left - right;
    }
    return a.name.localeCompare(b.name, "uk");
  });
}

export function KidsBoard({ me, active = true }: { me: MeResponse; active?: boolean }) {
  const { refreshMe } = useMeActions();
  const [kids, setKids] = useState<Kid[]>([]);
  const [parents, setParents] = useState<ParentOption[]>([]);
  const [canMap, setCanMap] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canSeePhones, setCanSeePhones] = useState(false);
  const [staffContacts, setStaffContacts] = useState<StaffContactDto[]>([]);
  const [name, setName] = useState("");
  const [birthday, setBirthday] = useState("");
  const [loading, setLoading] = useState(true);
  const [linkFor, setLinkFor] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<KidsSort>("name");
  const sortedKids = useMemo(() => sortKids(kids, sort), [kids, sort]);
  const canEditOwnPhone = isParentRole(me.user.role) || me.children.length > 0;
  const myKids = kids.filter((kid) => kid.canEditPhone);

  async function load() {
    const data = await api<{
      children: Kid[];
      parents: ParentOption[];
      canMap: boolean;
      canEdit: boolean;
      canSeePhones: boolean;
      staffContacts: StaffContactDto[];
    }>("/api/kids");
    setKids(data.children);
    setParents(data.parents);
    setCanMap(data.canMap);
    setCanEdit(data.canEdit);
    setCanSeePhones(data.canSeePhones);
    setStaffContacts(data.staffContacts ?? []);
    setLoading(false);
  }

  useEffect(() => {
    void load().catch((error) => {
      toast.error(error.message);
      setLoading(false);
    });
  }, []);

  useLiveReload("kids", load, active);

  async function addKid() {
    try {
      await api("/api/kids", {
        method: "POST",
        body: JSON.stringify({ name, birthday }),
      });
      setName("");
      setBirthday("");
      toast.success("Дитину додано до списку класу.");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося додати.");
    }
  }

  async function toggleActive(kid: Kid) {
    try {
      await api("/api/kids", {
        method: "PATCH",
        body: JSON.stringify({ id: kid.id, active: !kid.active }),
      });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося оновити.");
    }
  }

  async function savePhone(kid: Kid, next: string) {
    if (next === kid.phone) return;
    try {
      await api("/api/kids", {
        method: "PATCH",
        body: JSON.stringify({ id: kid.id, phone: next }),
      });
      setKids((prev) => prev.map((item) => (item.id === kid.id ? { ...item, phone: next } : item)));
      toast.success("Телефон дитини збережено.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося зберегти телефон.");
    }
  }

  async function saveBirthday(kid: Kid, next: string) {
    if (next === kid.birthday) return;
    try {
      await api("/api/kids", {
        method: "PATCH",
        body: JSON.stringify({ id: kid.id, birthday: next }),
      });
      setKids((prev) => prev.map((item) => (item.id === kid.id ? { ...item, birthday: next } : item)));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося зберегти день народження.");
    }
  }

  async function saveGender(kid: Kid, gender: "boy" | "girl") {
    if (gender === kid.gender) return;
    try {
      await api("/api/kids", {
        method: "PATCH",
        body: JSON.stringify({ id: kid.id, gender }),
      });
      setKids((prev) => prev.map((item) => (item.id === kid.id ? { ...item, gender } : item)));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося зберегти.");
    }
  }

  async function mapParent(childId: string, userId: string, action: "link" | "unlink") {
    try {
      await api("/api/people/link", {
        method: "POST",
        body: JSON.stringify({ childId, userId, action }),
      });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося змінити прив’язку.");
    }
  }

  async function pickChild(kid: Kid, focus: () => void) {
    try {
      const result = await api<{ url: string }>("/api/phone-share", {
        method: "POST",
        body: JSON.stringify({ target: kid.id }),
      });
      toast.message("Далі в чаті з ботом: скріпка → Контакт → оберіть дитину.");
      openTelegramUrl(result.url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося відкрити бота.");
      focus();
    }
  }

  if (loading) {
    return <StatusScreen loading title="Список класу" />;
  }

  return (
    <div className="space-y-4">
      {staffContacts.length > 0 ? <StaffContacts contacts={staffContacts} /> : null}
      {canEditOwnPhone || myKids.length > 0 ? (
        <section className="space-y-2 rounded-2xl bg-card px-4 py-3 ring-1 ring-foreground/8">
          {canEditOwnPhone ? (
            <OwnPhones
              name={me.user.name}
              phone={me.user.phone}
              onSaved={() => void refreshMe()}
              onRefresh={() => {
                void refreshMe();
                void load();
              }}
            />
          ) : null}
          {myKids.map((kid) => (
            <PhoneEditor
              key={kid.id}
              label={givenName(kid.name)}
              value={kid.phone}
              onCommit={(next) => savePhone(kid, next)}
              onPick={(focus) => void pickChild(kid, focus)}
              showContact
            />
          ))}
        </section>
      ) : null}
      <p className="text-sm leading-6 text-muted-foreground">
        Склад {me.settings.className}
        {canEdit
          ? ". Одну дитину можна прив’язати до кількох батьків. Адміністратора теж можна прив’язати, якщо він батько."
          : canSeePhones
            ? ". Дні народження, телефони батьків і дітей — щоб швидко знайти і набрати."
            : ". Номери вчителів — кнопка зверху. Список дітей класу."}
      </p>
      {canEdit ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Прізвище Ім’я"
              className="rounded-2xl"
            />
            <Button className="rounded-2xl" onClick={() => void addKid()}>
              Додати
            </Button>
          </div>
          <Input
            type="date"
            value={birthdayInputValue(birthday)}
            onChange={(event) => setBirthday(normalizeBirthday(event.target.value))}
            className="rounded-2xl"
            aria-label="День народження"
          />
        </div>
      ) : null}
      <div className="flex gap-1">
        {(
          [
            ["name", "А — Я"],
            ["birthday", "Дні народження"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setSort(id)}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-medium",
              sort === id ? "bg-primary text-primary-foreground" : "bg-accent text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <ul className="wide-cards">
        {sortedKids.map((kid) => {
          const available = parents.filter((person) => !kid.parents.some((parent) => parent.id === person.id));
          const nameRowPhones = joinPhones([
            kid.phone,
            canSeePhones && kid.parents.length === 0 ? kid.contactPhone : "",
          ]);
          return (
            <li key={kid.id} className="rounded-2xl bg-card px-4 py-3 ring-1 ring-foreground/8">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                    <p className={kid.active ? "font-medium" : "font-medium text-muted-foreground line-through"}>
                      {kid.name}
                    </p>
                    {canSeePhones || kid.canEditPhone ? <PhoneChips value={nameRowPhones} /> : null}
                  </div>
                  {kid.birthday ? (
                    <p className="mt-0.5 text-xs text-muted-foreground">{formatBirthdayUk(kid.birthday)}</p>
                  ) : canEdit ? (
                    <p className="mt-0.5 text-xs text-muted-foreground">День народження ще не вказано</p>
                  ) : null}
                </div>
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => void toggleActive(kid)}
                    className="shrink-0 text-xs font-medium text-primary"
                  >
                    {kid.active ? "Приховати" : "Повернути"}
                  </button>
                ) : null}
              </div>
              {canEdit ? (
                <Input
                  type="date"
                  value={birthdayInputValue(kid.birthday)}
                  onChange={(event) => void saveBirthday(kid, normalizeBirthday(event.target.value))}
                  className="mt-2 rounded-xl"
                  aria-label={`День народження ${kid.name}`}
                />
              ) : null}
              {canEdit ? (
                <div className="mt-2 flex gap-1">
                  {(
                    [
                      ["girl", "Дівчинка"],
                      ["boy", "Хлопчик"],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => void saveGender(kid, id)}
                      className={cn(
                        "rounded-full px-3 py-1 text-xs font-medium",
                        kid.gender === id ? "bg-primary text-primary-foreground" : "bg-accent text-foreground",
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
              {canSeePhones && kid.parents.length > 0 ? (
                <div className="mt-3 space-y-3">
                  {kid.parents.map((parent) => (
                    <PersonPhones
                      key={parent.id}
                      name={parent.name}
                      phones={parent.phone}
                      onUnlink={canMap ? () => void mapParent(kid.id, parent.id, "unlink") : undefined}
                    />
                  ))}
                </div>
              ) : null}
              {canMap && available.length > 0 && kid.active ? (
                <div className="mt-3 flex gap-2">
                  <select
                    className="h-9 min-w-0 flex-1 rounded-xl border border-input bg-background px-2 text-sm"
                    value={linkFor[kid.id] ?? ""}
                    onChange={(event) =>
                      setLinkFor((prev) => ({ ...prev, [kid.id]: event.target.value }))
                    }
                  >
                    <option value="">Прив’язати батьків…</option>
                    {available.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    disabled={!linkFor[kid.id]}
                    onClick={() => {
                      const userId = linkFor[kid.id];
                      if (!userId) return;
                      void mapParent(kid.id, userId, "link").then(() =>
                        setLinkFor((prev) => ({ ...prev, [kid.id]: "" })),
                      );
                    }}
                  >
                    OK
                  </Button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {isAdmin(me.user.role) ? (
        <p className="text-center text-xs text-muted-foreground">
          Батьки з’являються у «Люди», коли пишуть боту /start. Тут можна прив’язати їх до дитини. Себе як адміністратора можна прив’язати одразу.
        </p>
      ) : null}
    </div>
  );
}

function PersonPhones({
  name,
  phones,
  onUnlink,
}: {
  name: string;
  phones: string;
  onUnlink?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <p className="shrink-0 text-sm font-medium">{name}</p>
      {onUnlink ? (
        <button
          type="button"
          className="shrink-0 text-xs text-muted-foreground"
          onClick={onUnlink}
          aria-label={`Відв’язати ${name}`}
        >
          ×
        </button>
      ) : null}
      {phones ? <PhoneChips value={phones} /> : <p className="text-xs text-muted-foreground">Номера ще немає</p>}
    </div>
  );
}

function OwnPhones({
  name,
  phone,
  onSaved,
  onRefresh,
}: {
  name: string;
  phone: string;
  onSaved: () => void;
  onRefresh: () => void;
}) {
  const [value, setValue] = useState(phone);

  useEffect(() => {
    setValue(phone);
  }, [phone]);

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") onRefresh();
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [onRefresh]);

  async function saveParent(next: string) {
    if (next === normalizePhone(value)) return;
    const previous = value;
    setValue(next);
    try {
      await api("/api/me", { method: "PATCH", body: JSON.stringify({ phone: next }) });
      onSaved();
    } catch (error) {
      setValue(previous);
      toast.error(error instanceof Error ? error.message : "Не вдалося зберегти телефон.");
    }
  }

  function pickParent(focus: () => void) {
    const previous = normalizePhone(value);
    const started = requestOwnTelegramPhone((shared) => {
      if (!shared) return;
      void (async () => {
        for (let attempt = 0; attempt < 20; attempt += 1) {
          await wait(400);
          try {
            const me = await api<MeResponse>("/api/me");
            if (normalizePhone(me.user.phone) !== previous) {
              onSaved();
              return;
            }
          } catch {
            // Bot may still be writing.
          }
        }
        onRefresh();
      })();
    });
    if (!started) {
      toast.message("Відкрийте Mini App у Telegram, щоб поділитися номером.");
      focus();
    }
  }

  return (
    <PhoneEditor
      label={name}
      value={value}
      onCommit={saveParent}
      onPick={pickParent}
      showContact
    />
  );
}

function StaffContacts({ contacts }: { contacts: StaffContactDto[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="space-y-2">
      <Button
        type="button"
        variant="outline"
        className="h-11 w-full rounded-2xl"
        onClick={() => setOpen((next) => !next)}
        aria-expanded={open}
      >
        <PhoneIcon className="size-4" />
        {open ? "Сховати номери" : "Номери вчителів"}
      </Button>
      <SmoothReveal open={open}>
        <ul className="space-y-2 pt-2">
          {contacts.map((person) => {
            const href = phoneTelHref(person.phone);
            const inner = (
              <>
                <div className="min-w-0">
                  <p className="font-medium">{person.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{person.role}</p>
                </div>
                <span className="shrink-0 text-sm font-medium text-primary">{person.phone}</span>
              </>
            );
            return (
              <li key={person.id}>
                {href ? (
                  <a
                    href={href}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-card px-4 py-3 ring-1 ring-foreground/8"
                  >
                    {inner}
                  </a>
                ) : (
                  <div className="flex items-center justify-between gap-3 rounded-2xl bg-card px-4 py-3 ring-1 ring-foreground/8">
                    {inner}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </SmoothReveal>
    </section>
  );
}
